import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import api from "@/services/api";
import clientService from "@/services/client";
import { Client } from "@/types/client";
import { Loader2, Pencil, Plus, Trash2, Webhook as WebhookIcon, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const EVENT_OPTIONS = [
  { value: "ticket.created", label: "Ticket criado" },
  { value: "ticket.message", label: "Nova mensagem no ticket" },
  { value: "whatsapp.message.received", label: "Mensagem de WhatsApp recebida" },
  { value: "whatsapp.message.sent", label: "Mensagem de WhatsApp enviada" },
];

const CONDITION_OPTIONS: Array<{ key: keyof WebhookConditions; label: string }> = [
  { key: "noCompany", label: "Contato sem empresa vinculada" },
  { key: "hasCompany", label: "Contato com empresa vinculada" },
  { key: "noCompanyTags", label: "Empresa do contato sem nenhuma tag" },
  { key: "noContactTags", label: "Contato sem nenhuma tag" },
];

interface WebhookConditions {
  noCompany?: boolean;
  hasCompany?: boolean;
  noCompanyTags?: boolean;
  noContactTags?: boolean;
}

interface WebhookTargetItem {
  _id: string;
  name?: string;
  url: string;
  active: boolean;
  events: string[];
  contactId?: string;
  contactName?: string;
  conditions?: WebhookConditions;
  hasAuthHeader: boolean;
  createdAt: string;
}

interface DeliveryItem {
  _id: string;
  event: string;
  url: string;
  payload: unknown;
  status: "pending" | "success" | "failed";
  attempts: number;
  lastStatusCode?: number;
  lastError?: string;
  lastResponseBody?: string;
  lastAttemptAt?: string;
  createdAt: string;
}

function formatDate(value?: string) {
  if (!value) return "—";
  return new Date(value).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" });
}

function eventLabel(value: string) {
  return EVENT_OPTIONS.find((e) => e.value === value)?.label || value;
}

// ─── Seletor de contato (busca simples por nome/telefone) ─────────────────────

function ContactPicker({
  value,
  onChange,
}: {
  value: { id: string; name: string } | null;
  onChange: (contact: { id: string; name: string } | null) => void;
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<Client[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setLoading(true);
      const [data] = await clientService.getAll(query.trim(), 1, 10);
      setResults(data?.result || []);
      setLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  if (value) {
    return (
      <div className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2">
        <span className="text-sm">{value.name}</span>
        <button type="button" onClick={() => onChange(null)} className="text-muted-foreground hover:text-foreground">
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <Input
        placeholder="Buscar contato por nome ou telefone..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && query.trim() && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-border bg-popover shadow-md">
          {loading ? (
            <div className="p-2 text-sm text-muted-foreground">Buscando...</div>
          ) : results.length === 0 ? (
            <div className="p-2 text-sm text-muted-foreground">Nenhum contato encontrado</div>
          ) : (
            results.map((c) => (
              <button
                key={c._id}
                type="button"
                className="block w-full px-3 py-2 text-left text-sm hover:bg-accent"
                onMouseDown={() => {
                  onChange({ id: c._id, name: c.name });
                  setQuery("");
                  setOpen(false);
                }}
              >
                {c.name} {c.phone ? <span className="text-muted-foreground">· {c.phone}</span> : null}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ─── Formulário de criação/edição ───────────────────────────────────────────

function WebhookFormDialog({
  open,
  onOpenChange,
  onSaved,
  target,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  onSaved: () => void;
  /** Presente = edição desse destino; ausente = criação. */
  target?: WebhookTargetItem | null;
}) {
  const isEdit = Boolean(target);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [authHeader, setAuthHeader] = useState("");
  const [clearAuthHeader, setClearAuthHeader] = useState(false);
  const [events, setEvents] = useState<string[]>([]);
  const [contact, setContact] = useState<{ id: string; name: string } | null>(null);
  const [conditions, setConditions] = useState<WebhookConditions>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(target?.name || "");
    setUrl(target?.url || "");
    setAuthHeader("");
    setClearAuthHeader(false);
    setEvents(target?.events ? [...target.events] : []);
    setContact(target?.contactId ? { id: target.contactId, name: target.contactName || target.contactId } : null);
    setConditions(target?.conditions || {});
  }, [open, target]);

  function toggleEvent(value: string) {
    setEvents((prev) => (prev.includes(value) ? prev.filter((e) => e !== value) : [...prev, value]));
  }

  function toggleCondition(key: keyof WebhookConditions) {
    setConditions((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function handleSave() {
    if (!url.trim()) {
      toast.error("Informe a URL de destino");
      return;
    }
    if (!events.length) {
      toast.error("Selecione pelo menos um evento");
      return;
    }
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: name.trim() || undefined,
        url: url.trim(),
        events,
      };
      // Condições só valem pra webhook geral — com contato específico selecionado nem manda o campo.
      if (!contact) body.conditions = conditions;
      if (isEdit) {
        body.contactId = contact?.id || null;
        if (clearAuthHeader) body.authHeader = "";
        else if (authHeader.trim()) body.authHeader = authHeader.trim();
        await api.put(`/app/webhooks/${target!._id}`, body);
        toast.success("Webhook atualizado");
      } else {
        body.contactId = contact?.id;
        body.authHeader = authHeader.trim() || undefined;
        await api.post("/app/webhooks", body);
        toast.success("Webhook criado");
      }
      onOpenChange(false);
      onSaved();
    } catch {
      toast.error(isEdit ? "Não foi possível atualizar o webhook" : "Não foi possível criar o webhook");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar webhook" : "Novo webhook"}</DialogTitle>
          <DialogDescription>
            Sem contato selecionado, esse webhook é o <strong>geral</strong> da organização (fallback). Com um
            contato selecionado, ele <strong>substitui</strong> o geral só pros eventos desse contato.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Nome (opcional)</Label>
            <Input placeholder="Ex.: CRM do time comercial" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>URL de destino</Label>
            <Input placeholder="https://..." value={url} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Cabeçalho Authorization (opcional)</Label>
            <Input
              placeholder={isEdit && target?.hasAuthHeader && !clearAuthHeader ? "•••• (mantém o atual se deixar em branco)" : "Ex.: Bearer crsr_abc123..."}
              value={authHeader}
              onChange={(e) => {
                setAuthHeader(e.target.value);
                if (e.target.value) setClearAuthHeader(false);
              }}
              disabled={clearAuthHeader}
            />
            {isEdit && target?.hasAuthHeader ? (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="webhook-clear-auth"
                  checked={clearAuthHeader}
                  onCheckedChange={(v) => {
                    setClearAuthHeader(Boolean(v));
                    if (v) setAuthHeader("");
                  }}
                />
                <Label htmlFor="webhook-clear-auth" className="text-xs font-normal text-muted-foreground">
                  Remover autenticação atual
                </Label>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">Enviado exatamente como digitado no header Authorization.</p>
            )}
          </div>
          <div className="space-y-2">
            <Label>Contato específico (opcional)</Label>
            <ContactPicker
              value={contact}
              onChange={(c) => {
                setContact(c);
                if (c) setConditions({});
              }}
            />
          </div>
          {!contact && (
            <div className="space-y-2">
              <Label>Condições (opcional)</Label>
              <p className="text-xs text-muted-foreground">
                Restringe esse webhook geral a disparar só quando TODAS as condições marcadas forem
                verdadeiras. Sem nenhuma marcada, dispara pra qualquer evento sem webhook de contato
                específico.
              </p>
              <div className="space-y-2">
                {CONDITION_OPTIONS.map((opt) => (
                  <div key={opt.key} className="flex items-center gap-2">
                    <Checkbox
                      id={`webhook-condition-${opt.key}`}
                      checked={Boolean(conditions[opt.key])}
                      onCheckedChange={() => toggleCondition(opt.key)}
                    />
                    <Label htmlFor={`webhook-condition-${opt.key}`} className="text-sm font-normal">
                      {opt.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label>Eventos</Label>
            <div className="space-y-2">
              {EVENT_OPTIONS.map((opt) => (
                <div key={opt.value} className="flex items-center gap-2">
                  <Checkbox
                    id={`webhook-event-${opt.value}`}
                    checked={events.includes(opt.value)}
                    onCheckedChange={() => toggleEvent(opt.value)}
                  />
                  <Label htmlFor={`webhook-event-${opt.value}`} className="text-sm font-normal">
                    {opt.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : isEdit ? "Salvar alterações" : "Criar webhook"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Histórico de disparos de um destino ───────────────────────────────────

const STATUS_LABEL: Record<string, string> = {
  success: "Entregue",
  failed: "Falhou",
  pending: "Pendente",
};

function statusBadgeClass(status: string) {
  if (status === "success") return "bg-green-500/10 text-green-600 border-green-500/20";
  if (status === "failed") return "bg-destructive/10 text-destructive border-destructive/20";
  return "bg-amber-500/10 text-amber-600 border-amber-500/20";
}

function prettyJson(value: unknown) {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function DeliveryRow({ delivery }: { delivery: DeliveryItem }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded-md border border-border px-3 py-2 text-sm">
      <button type="button" className="flex w-full items-center justify-between gap-2 text-left" onClick={() => setExpanded((v) => !v)}>
        <span className="font-medium">{eventLabel(delivery.event)}</span>
        <Badge variant="outline" className={statusBadgeClass(delivery.status)}>
          {STATUS_LABEL[delivery.status] || delivery.status}
        </Badge>
      </button>
      <p className="mt-1 text-xs text-muted-foreground">
        {formatDate(delivery.lastAttemptAt || delivery.createdAt)} · {delivery.attempts} tentativa{delivery.attempts !== 1 ? "s" : ""}
        {delivery.lastStatusCode ? ` · HTTP ${delivery.lastStatusCode}` : ""}
      </p>
      {delivery.lastError && <p className="mt-1 text-xs text-destructive">{delivery.lastError}</p>}
      {expanded && (
        <div className="mt-3 space-y-3">
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">URL de destino</p>
            <p className="break-all rounded bg-muted px-2 py-1 text-xs">{delivery.url}</p>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Payload enviado</p>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted px-2 py-1 text-xs">
              {prettyJson(delivery.payload)}
            </pre>
          </div>
          <div>
            <p className="mb-1 text-xs font-medium text-muted-foreground">Resposta recebida</p>
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-all rounded bg-muted px-2 py-1 text-xs">
              {delivery.lastResponseBody || "(sem corpo de resposta)"}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}

function DeliveriesDialog({ targetId, open, onOpenChange }: { targetId: string | null; open: boolean; onOpenChange: (v: boolean) => void }) {
  const [deliveries, setDeliveries] = useState<DeliveryItem[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [eventFilter, setEventFilter] = useState<string>("all");

  useEffect(() => {
    if (!open || !targetId) return;
    setDeliveries(null);
    setStatusFilter("all");
    setEventFilter("all");
    api
      .get<DeliveryItem[]>(`/app/webhooks/${targetId}/deliveries`)
      .then((res) => setDeliveries(res.data))
      .catch(() => setDeliveries([]));
  }, [open, targetId]);

  const filtered = useMemo(() => {
    if (!deliveries) return null;
    return deliveries.filter((d) => {
      if (statusFilter !== "all" && d.status !== statusFilter) return false;
      if (eventFilter !== "all" && d.event !== eventFilter) return false;
      return true;
    });
  }, [deliveries, statusFilter, eventFilter]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Histórico de disparos</DialogTitle>
          <DialogDescription>
            Últimos 200 — inclui tentativas de retry. Clique num disparo pra ver o payload enviado e a
            resposta completa.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-40 text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="success">Entregue</SelectItem>
              <SelectItem value="failed">Falhou</SelectItem>
              <SelectItem value="pending">Pendente</SelectItem>
            </SelectContent>
          </Select>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="w-56 text-sm">
              <SelectValue placeholder="Evento" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os eventos</SelectItem>
              {EVENT_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="max-h-[28rem] space-y-2 overflow-y-auto">
          {filtered === null ? (
            <Skeleton className="h-10 w-full" />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum disparo encontrado com esses filtros.</p>
          ) : (
            filtered.map((d) => <DeliveryRow key={d._id} delivery={d} />)
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Página ─────────────────────────────────────────────────────────────────

export default function WebhooksSettings() {
  const [targets, setTargets] = useState<WebhookTargetItem[] | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<WebhookTargetItem | null>(null);
  const [deliveriesTargetId, setDeliveriesTargetId] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get<WebhookTargetItem[]>("/app/webhooks");
      setTargets(res.data);
    } catch {
      setTargets([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handleToggleActive(target: WebhookTargetItem) {
    try {
      await api.put(`/app/webhooks/${target._id}`, { active: !target.active });
      await load();
    } catch {
      toast.error("Não foi possível atualizar");
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await api.delete(`/app/webhooks/${id}`);
      toast.success("Webhook removido");
      await load();
    } catch {
      toast.error("Não foi possível remover");
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <WebhookIcon className="h-5 w-5 text-primary" />
            <h1 className="text-2xl font-bold">Webhooks</h1>
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Dispara um POST pra uma URL quando chega mensagem de WhatsApp ou acontece algo num
            ticket. Um webhook específico de um contato substitui o geral pros eventos desse
            contato.
          </p>
        </div>
        <Button className="gap-2" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4" />
          Novo webhook
        </Button>
      </div>

      {targets === null ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : targets.length === 0 ? (
        <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">
          Nenhum webhook configurado ainda.
        </div>
      ) : (
        <div className="space-y-3">
          {targets.map((t) => (
            <div key={t._id} className="rounded-lg border border-border p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{t.name || "(sem nome)"}</span>
                    {t.contactId ? (
                      <Badge variant="outline">Específico: {t.contactName || t.contactId}</Badge>
                    ) : (
                      <Badge variant="outline">Geral (fallback)</Badge>
                    )}
                  </div>
                  <p className="mt-1 truncate text-sm text-muted-foreground">{t.url}</p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {t.events.map((e) => (
                      <Badge key={e} variant="outline" className="text-xs">
                        {eventLabel(e)}
                      </Badge>
                    ))}
                  </div>
                  {CONDITION_OPTIONS.some((opt) => t.conditions?.[opt.key]) && (
                    <div className="mt-1 flex flex-wrap gap-1">
                      {CONDITION_OPTIONS.filter((opt) => t.conditions?.[opt.key]).map((opt) => (
                        <Badge key={opt.key} variant="outline" className="text-xs text-amber-600 border-amber-500/30">
                          {opt.label}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <Switch checked={t.active} onCheckedChange={() => handleToggleActive(t)} />
                  <Button variant="outline" size="sm" onClick={() => setDeliveriesTargetId(t._id)}>
                    Histórico
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => setEditTarget(t)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDelete(t._id)}
                    disabled={deletingId === t._id}
                  >
                    {deletingId === t._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <WebhookFormDialog open={createOpen} onOpenChange={setCreateOpen} onSaved={load} />
      <WebhookFormDialog
        open={!!editTarget}
        onOpenChange={(v) => !v && setEditTarget(null)}
        onSaved={load}
        target={editTarget}
      />
      <DeliveriesDialog targetId={deliveriesTargetId} open={!!deliveriesTargetId} onOpenChange={(v) => !v && setDeliveriesTargetId(null)} />
    </>
  );
}
